import {axisBottom, axisLeft} from 'd3-axis';
import {IAugmentedJQuery} from 'angular';
import {ScaleTime, scaleTime, scaleLinear} from 'd3-scale';
import {select} from 'd3-selection';
import * as moment from 'moment';
import {line, curveLinear} from 'd3-shape';
import {IChartColorService} from '../../utils/chart-color.service';

export interface ITimeseriesCurve {
  path: string|null;
  strokeColor: string;
}

export interface ITimeseriesPoint {
  x: number;
  y: number;
}

export interface IHydrodataTimeseries {
  data: IHydrodataDatum[];
  label: string;
  highlighted?: boolean;
  current?: boolean;
  id?: string;
}

export interface IHydrodataDatum {
  date: moment.Moment;
  hydrodata: any;
}

export interface ILegendEntry {
  color: string;
  label: string;
}

export class TimeseriesController {
  static readonly $inject = ['$element', 'chartColorService'];
  /**
   * The hydrological year starts in October.
   */
  private static readonly CURRENT_HYDROYEAR =
      moment().month() >= 9 ? moment().year() + 1 : moment().year();

  /**
   * Displayed curves.
   */
  curves: ITimeseriesCurve[];
  /**
   * Timeseries to draw.
   */
  timeseries: IHydrodataTimeseries[]|null;
  /**
   * Data type for unit definition.
   */
   hydrodataType: string;
  /**
   * Legend entries.
   */
  legendEntries: ILegendEntry[];

  /**
   * Default width of the SVG, in pixels.
   */
  get width() {
    return 960;
  }

  /**
   * Default height of the SVG, in pixels.
   */
  get height() {
    return 540;
  }

  /**
   * Minor margin used in the top and right parts of the graph.
   */
  get minorMargin() {
    return 20;
  }

  /**
   * Major margin used in the left and bottom parts of the graph.
   */
  get majorMargin() {
    return 60;
  }

  /**
   * Calculated width of the inner drawing area.
   */
  get innerWidth() {
    return this.width - this.minorMargin - this.majorMargin;
  }

  /**
   * Calculated height of the inner drawing area.
   */
  get innerHeight() {
    return this.height - this.minorMargin - this.majorMargin;
  }

  /**
   * Legend width.
   */
  get legendWidth() {
    return 150;
  }

  /**
   * Calculated legend height.
   */
  get legendHeight() {
    return this.legendEntries.length * 20 + 2;
  }

  /**
   * Legend rounded corner radius.
   */
  get legendRadius() {
    return 2;
  }

  /**
   * X-axis time scale.
   */
  private readonly timeScale: ScaleTime<number, number>;
  /**
   * Whether the initial drawn is complete.
   */
  private drawn: boolean;

  constructor(private $element: IAugmentedJQuery,
              private chartColorService: IChartColorService) {
    this.curves = [];
    this.legendEntries = [];
    this.drawn = false;

    const firstDayInHydroyear = moment()
        .year(TimeseriesController.CURRENT_HYDROYEAR - 1)
        .month(9) // October
        .startOf('month');
    const lastDayInHydroyear = moment()
        .year(TimeseriesController.CURRENT_HYDROYEAR)
        .month(8) // September
        .endOf('month')
    this.timeScale = scaleTime()
        .domain([
          firstDayInHydroyear.toDate(),
          lastDayInHydroyear.toDate()
        ])
        .range([0, this.innerWidth]);
  }

  $postLink() {
    this.draw();
    this.drawn = true;
  }

  $onChanges() {
    this.timeseries = this.timeseries || [];
    if (this.drawn) {
      this.update();
    }
  }

  private draw() {
    const element = select(this.$element[0]);
    const axisElement = element.select('.x.axis');
    const xAxis = axisBottom(this.timeScale)
        .ticks(12)
        .tickSizeInner(-this.innerHeight)
        .tickSizeOuter(0)
        .tickFormat(date => moment(date).format('MMM'));
    axisElement.call(xAxis);
  }

  private update() {
    const element = select(this.$element[0]);
    const axisElement = element.select('.y.axis');
    const yScale = scaleLinear()
        .domain([this.hydrodataType === 'temperature' ? this.determineMinTemperature() : 0, this.determineMaxDischarge()])
        .range([this.innerHeight, 0]);
    const yAxis = axisLeft(yScale)
        .tickSizeOuter(0)
        .tickSizeInner(-this.innerWidth)
        .ticks(10);
    axisElement.call(yAxis);

    const lineGenerator = line<IHydrodataDatum>()
        .x(datum => {
          return this.timeScale(this.normalizeDate(datum.date));
        })
        .y(datum => yScale(datum.hydrodata))
        .curve(curveLinear);

    this.curves = this.timeseries.map((timeseries, index) => {
      return {
        path: lineGenerator(timeseries.data),
        strokeColor: this.chartColorService.getColors(index).backgroundColor
      };
    });
    this.legendEntries = this.curves.map((curve, index) => {
      return {
        label: this.timeseries[index].label,
        color: curve.strokeColor
      };
    });
  }

  private normalizeDate(date: moment.Moment): Date {
    date = date.clone();
    if (date.month() < 9) {
      date.year(TimeseriesController.CURRENT_HYDROYEAR);
    } else {
      date.year(TimeseriesController.CURRENT_HYDROYEAR - 1);
    }
    return date.toDate();
  }

  private determineMaxDischarge() {
    const margin = 5;
    const max = Math.max(...this.timeseries.map(timeseries => {
      return Math.max(...timeseries.data.map(datum => datum.hydrodata));
    }));
    return max + margin;
  }

  private determineMinTemperature() {
    const margin = 5;
    const min = Math.min(...this.timeseries.map(timeseries => {
        return Math.min(...timeseries.data.map(datum => datum.hydrodata));
    }));
    return min - margin;
  }
}



/** WEBPACK FOOTER **
 ** ./app/hydromet/timeseries/timeseries.controller.ts
 **/